class ProductStickyBar extends HTMLElement {
  constructor() {
    super();
    this.productContainer = document.querySelector('[data-product-container] .product-form') || null;
    this.addTocarBtn = this.querySelector('[data-sticky-addtocart]') || null;
    this.globaleHeader = document.querySelector('.section-header');
    this.termsCheckbox = document.querySelector('[data-pdp-tnc]');
    this.productColor = this.querySelector('[data-sticky-product-color]');
    this.productSize = this.querySelector('[data-sticky-product-size]');
    this.metafieldMap = this.parseMetafieldMap(this.dataset.metafieldMap || '');
  }

  connectedCallback() {
    // Set up click handler for sticky bar button
    if (this.addTocarBtn) {
      this.addTocarBtn.addEventListener('click', this.handleAddToCart.bind(this));
    }

    // Set up intersection observer if product container exists
    if (this.productContainer) {
      this.observeProductContainer();
    }

    // Listen for variant change and ATC events
    if (typeof eventBus !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      eventBus.subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      eventBus.subscribe(PUB_SUB_EVENTS.productATC, this.handleProductATC.bind(this));
    }
  }

  handleAddToCart(e) {
    // If button opens a drawer
    if (this.addTocarBtn.hasAttribute('data-toggleDrawer')) {
      // If dynamically added (by handleVariantChange), call openDrawer directly since drawer's native handler isn't bound
      // If not dynamic, let drawer's native handler work (don't interfere)
      if (this.addTocarBtn.hasAttribute('data-dynamic-drawer')) {
        e.preventDefault();
        e.stopPropagation();
        const drawerId = this.addTocarBtn.getAttribute('data-toggleDrawer');
        const drawer = document.getElementById(drawerId);
        if (drawer && typeof drawer.openDrawer === 'function') {
          drawer.openDrawer(this.addTocarBtn);
        }
      }
      return;
    }

    // If button has needs-selection flag, scroll to variant picker instead of submitting
    if (this.addTocarBtn.hasAttribute('data-needs-selection')) {
      e.preventDefault();
      e.stopPropagation();
      this.scrollToVariantPicker();
      return false;
    }

    if (this.termsCheckbox && !this.termsCheckbox.checked) {
      e.preventDefault();
      e.stopPropagation();
      this.addTocarBtn.classList.remove('loading');
      this.termsCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Show error message if not already shown
      const tncContainer = this.termsCheckbox.closest('.pdp-tnc');
      if (tncContainer && !tncContainer.querySelector('.terms-error')) {
        const errorMessage = document.createElement('div');
        errorMessage.classList.add('terms-error', 'text-danger', 'mt-2');
        errorMessage.textContent = 'Please accept the Terms & Conditions';
        tncContainer.appendChild(errorMessage);
      }
      return false;
    }

    // Virtual Race validation - check if user has selected an option
    if (window.globalSpace && window.globalSpace.templateSuffix === 'virtual-race') {
      if (!this.virtualRaceDefaultOptionValidation()) {
        e.preventDefault();
        e.stopPropagation();
        this.addTocarBtn.classList.remove('loading');
        return false;
      }
    }

    this.addTocarBtn.classList.add('loading');
  }

  scrollToVariantPicker() {
    const variantPicker = document.querySelector('variant-selects');

    if (variantPicker) {
      variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Try to focus the first unselected option group
      let firstUnselected = null;
      try {
        firstUnselected = variantPicker.querySelector('fieldset:not(:has(input:checked)), .product-form__input--dropdown:not(:has(input:checked))');
      } catch (e) {
        // Fallback for browsers without :has() support
        const fieldsets = variantPicker.querySelectorAll('fieldset');
        const dropdowns = variantPicker.querySelectorAll('.product-form__input--dropdown');
        [...fieldsets, ...dropdowns].some(container => {
          if (!container.querySelector('input:checked')) {
            firstUnselected = container;
            return true;
          }
          return false;
        });
      }

      if (firstUnselected) {
        const firstInput = firstUnselected.querySelector('input, button');
        if (firstInput) {
          setTimeout(() => {
            firstInput.focus();
          }, 500);
        }
      }
    }
  }

  handleVariantChange(eventData) {
    const { container, variant, sectionRendered } = eventData.data;

    if (!container.hasAttribute('data-product-container')) {
      return;
    }

    // Handle case when no variant is selected
    if (!variant) {
      if (this.addTocarBtn) {
        this.addTocarBtn.setAttribute('data-needs-selection', 'true');
        this.addTocarBtn.setAttribute('type', 'button');
        this.addTocarBtn.removeAttribute('form');
        this.addTocarBtn.removeAttribute('data-toggleDrawer');
        this.addTocarBtn.removeAttribute('data-dynamic-drawer');
        this.addTocarBtn.classList.add('btn-disabled-selection');
        const addText = this.addTocarBtn.querySelector('[data-stick-atc-text]');
        if (addText) {
          addText.textContent = 'Select All Options';
        }
      }
      return;
    }

    // Only update color/size display on initial event (not sectionRendered follow-up)
    if (!sectionRendered) {
      const options = Array.from(container.querySelectorAll('[data-optionindex]')).map(
        option => option.querySelector('legend')?.textContent.trim().toLowerCase() || ''
      );

      // Color
      const colorIndex = options.findIndex(opt => opt.includes('color'));
      if (colorIndex !== -1 && this.productColor && variant[`option${colorIndex + 1}`]) {
        const originalValue = variant[`option${colorIndex + 1}`];
        const displayValue = this.metafieldMap[originalValue] || originalValue.toLowerCase();
        this.productColor.textContent = displayValue;
        const colorSpan = this.productColor.previousElementSibling;
        const selectedColorLabel = container.querySelector(`.swatch-label[title="${originalValue}"]`);
        if (selectedColorLabel && colorSpan) {
          colorSpan.style.backgroundColor = selectedColorLabel.style.backgroundColor;
        }
      }

      // Size
      const sizeIndex = options.findIndex(opt => opt.includes('size'));
      if (sizeIndex !== -1 && this.productSize && variant[`option${sizeIndex + 1}`]) {
        const originalValue = variant[`option${sizeIndex + 1}`];
        const displayValue = this.metafieldMap[originalValue] || originalValue;
        this.productSize.textContent = displayValue;
      }
    }

    // Update button state based on variant availability
    // On sectionRendered, re-read the form attribute to get updated personalization status
    if (this.addTocarBtn) {
      const addText = this.addTocarBtn.querySelector('[data-stick-atc-text]');
      const productForm = document.querySelector('[data-type="add-to-cart-form"]');
      const formId = productForm?.getAttribute('id') || '';
      const hasPersonalization = productForm?.dataset.requiredPersonalization === 'true';

      if (variant.available) {
        // Remove needs-selection state
        this.addTocarBtn.removeAttribute('data-needs-selection');
        this.addTocarBtn.removeAttribute('disabled');
        this.addTocarBtn.classList.remove('disabled', 'btn-disabled-selection');

        if (hasPersonalization) {
          // Personalization required - set up for drawer
          this.addTocarBtn.setAttribute('type', 'button');
          this.addTocarBtn.setAttribute('data-toggleDrawer', 'component-personalization-drawer');
          this.addTocarBtn.setAttribute('data-dynamic-drawer', 'true');
          this.addTocarBtn.removeAttribute('form');
          if (addText) {
            addText.textContent = 'Personalize & Add to Cart';
          }
        } else {
          // No personalization - regular submit button
          this.addTocarBtn.setAttribute('type', 'submit');
          this.addTocarBtn.removeAttribute('data-toggleDrawer');
          this.addTocarBtn.removeAttribute('data-dynamic-drawer');
          if (formId) {
            this.addTocarBtn.setAttribute('form', formId);
          }
          if (addText) {
            addText.textContent = window.variantStrings?.addToCart || 'Add to Cart';
          }
        }
      } else {
        // Variant not available (sold out)
        this.addTocarBtn.removeAttribute('data-needs-selection');
        this.addTocarBtn.removeAttribute('data-toggleDrawer');
        this.addTocarBtn.removeAttribute('data-dynamic-drawer');
        this.addTocarBtn.setAttribute('disabled', true);
        this.addTocarBtn.classList.add('disabled');
        this.addTocarBtn.classList.remove('btn-disabled-selection');
        if (addText) {
          addText.textContent = window.variantStrings?.soldOut || 'Sold Out';
        }
      }
    }
  }

  handleProductATC() {
    if (this.addTocarBtn) {
      if (!this.termsCheckbox || this.termsCheckbox.checked) {
        this.addTocarBtn.classList.remove('loading');
      }
    }
  }

  /** Virtual Race default option to be unselected */
  virtualRaceDefaultOptionValidation() {
    if (window.globalSpace && window.globalSpace.templateSuffix === 'virtual-race' && this.productContainer) {
      let return_value = true;
      let firstInvalidSelector = null;
      this.productContainer.querySelectorAll('input[name^="options["][data-select-field]').forEach(input => {
        if (input.checked) {
          if (input.closest('.select')) {
            const selectElement = input.closest('.select');
            selectElement.classList.add('error-please-select');
            setTimeout(() => {
              selectElement.classList.remove('error-please-select');
            }, 2400);
            if (!firstInvalidSelector) {
              firstInvalidSelector = selectElement;
            }
          }
          return_value = false;
        }
      });
      // Scroll to the first invalid selector if validation fails
      if (!return_value && firstInvalidSelector) {
        firstInvalidSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return return_value;
    }
    return true; // Return true if not virtual race template
  }

  parseMetafieldMap(metafieldString) {
    const map = {};
    if (metafieldString) {
      metafieldString.split(',').forEach(pair => {
        const [key, value] = pair.split(':');
        if (key && value) {
          map[key.trim()] = value.trim();
        }
      });
    }
    return map;
  }

  observeProductContainer() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          this.classList.add('open');
        } else {
          this.classList.remove('open');
        }
      },
      { threshold: 0 }
    );
    observer.observe(this.productContainer);
  }
}

customElements.define('product-sticky-bar', ProductStickyBar);
